import React, { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { isBookingOpen, bookingMessage } from "../utils/bookingLock";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isInitiativesOpen, setIsInitiativesOpen] = useState(false);
  const [isAccessoriesOpen, setIsAccessoriesOpen] = useState(false);

  /* Scroll effect */
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Reset menus on route change */
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsInitiativesOpen(false);
    setIsAccessoriesOpen(false);
  }, [location.pathname]);

  const goTo = (path) => {
    setIsMobileMenuOpen(false);
    setIsInitiativesOpen(false);
    setIsAccessoriesOpen(false);

    if (path === "/" && location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    navigate(path);
  };

  const handlePreBook = () => {
    if (!isBookingOpen()) {
      alert(bookingMessage());
      return;
    }
    navigate("/prebook");
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-20">

          {/* LOGO + SLOGAN (FIXED WIDTH, NO OVERLAP) */}
          <div
            onClick={() => goTo("/")}
            className="flex items-center gap-3 cursor-pointer flex-shrink-0 w-[260px]"
          >
            <img
              src="/assets/aerzen-logo.png"
              alt="AERZEN AIR™"
              className="h-12 w-auto object-contain"
            />
            <span className="hidden xl:block text-xs text-gray-600 whitespace-nowrap">
              Breathe Pure • Live Better
            </span>
          </div>

          {/* DESKTOP NAV */}
          <nav className="hidden md:flex items-center gap-6 ml-auto whitespace-nowrap">
            <button onClick={() => goTo("/")} className="nav-link">Home</button>
            <button onClick={() => goTo("/products")} className="nav-link">Products</button>
            <button onClick={() => goTo("/institutional-solutions")} className="nav-link">
              Institutional Solutions
            </button>

            {/* INITIATIVES */}
            <div className="relative">
              <button
                onClick={() => setIsInitiativesOpen(!isInitiativesOpen)}
                className="flex items-center gap-1 nav-link"
              >
                Initiatives <ChevronDown className="h-4 w-4" />
              </button>
              {isInitiativesOpen && (
                <div className="absolute top-8 left-0 bg-white shadow-lg rounded-lg w-64 z-50">
                  <button
                    onClick={() => goTo("/initiatives/airshield-transit-zone")}
                    className="block w-full text-left px-4 py-3 hover:bg-gray-100 text-sm"
                  >
                    AirShield™ Transit Zone
                  </button>
                  <button
                    onClick={() => goTo("/mafa")}
                    className="block w-full text-left px-4 py-3 hover:bg-gray-100 text-sm"
                  >
                    MAFA (R&D)
                  </button>
                </div>
              )}
            </div>

            {/* ACCESSORIES */}
            <div className="relative">
              <button
                onClick={() => setIsAccessoriesOpen(!isAccessoriesOpen)}
                className="flex items-center gap-1 nav-link"
              >
                Accessories <ChevronDown className="h-4 w-4" />
              </button>
              {isAccessoriesOpen && (
                <div className="absolute top-8 left-0 bg-white shadow-lg rounded-lg w-56 z-50">
                  <button
                    onClick={() => goTo("/aq-sense")}
                    className="block w-full text-left px-4 py-3 hover:bg-gray-100 text-sm"
                  >
                    AQ Sense
                  </button>
                </div>
              )}
            </div>

            <button onClick={() => goTo("/dealer-enquiry")} className="nav-link">
              Dealer Enquiry
            </button>
            <button onClick={() => goTo("/vision")} className="nav-link">Vision</button>
            <button onClick={() => goTo("/mission")} className="nav-link">Mission</button>
            <button onClick={() => goTo("/environmental-responsibility")} className="nav-link">
              Sustainability
            </button>
            <button onClick={() => goTo("/contact")} className="nav-link">Contact</button>

            <Button
              onClick={handlePreBook}
              className="bg-green-600 hover:bg-green-700 text-white px-6"
            >
              Pre-Book Now
            </Button>
          </nav>

          {/* MOBILE TOGGLE */}
          <button
            className="md:hidden ml-auto p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>

        {/* MOBILE MENU */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t py-4">
            <nav className="flex flex-col space-y-4">
              <button onClick={() => goTo("/")}>Home</button>
              <button onClick={() => goTo("/products")}>Products</button>
              <button onClick={() => goTo("/institutional-solutions")}>
                Institutional Solutions
              </button>
              <button onClick={() => goTo("/initiatives/airshield-transit-zone")}>
                AirShield Transit Zone
              </button>
              <button onClick={() => goTo("/mafa")}>MAFA</button>
              <button onClick={() => goTo("/aq-sense")}>AQ Sense</button>
              <button onClick={() => goTo("/dealer-enquiry")}>Dealer Enquiry</button>
              <button onClick={() => goTo("/vision")}>Vision</button>
              <button onClick={() => goTo("/mission")}>Mission</button>
              <button onClick={() => goTo("/environmental-responsibility")}>
                Sustainability
              </button>
              <button onClick={() => goTo("/contact")}>Contact</button>

              <Button
                onClick={handlePreBook}
                className="bg-green-600 hover:bg-green-700 text-white w-full"
              >
                Pre-Book Now
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
